import { configureStore } from '@reduxjs/toolkit';
import { createWrapper } from 'next-redux-wrapper';
import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE, createTransform, persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { IS_DEVELOPMENT } from '@/constants/env';
import { ServerStatus } from '@/models/globalModels';

import activityApi from './activity/api';
import languagesApi from './languages/api';
import rootReducer, { RootState } from './rootReducer';
import shareApi from './share/api';
import statsApi from './stats/api';
import stripeConnectApi from './stripeConnect/api';
import tablesApi from './tables/api';

const resetFetchingStatuses = (state: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(state).map(([key, value]) =>
      key.endsWith('Status') && value === ServerStatus.FETCHING ? [key, ServerStatus.IDLE] : [key, value],
    ),
  );

const authStatusTransform = createTransform(resetFetchingStatuses, resetFetchingStatuses, { whitelist: ['auth'] });

const persistConfig = {
  key: 'root',
  storage,
  blacklist: [shareApi.reducerPath, statsApi.reducerPath, stripeConnectApi.reducerPath],
  whitelist: ['auth', 'layout', 'customizations'], // stores to persist
  transforms: [authStatusTransform],
};
const persistedReducer = persistReducer<RootState>(persistConfig, rootReducer as any);

const makeStore = () =>
  configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat([
        activityApi.middleware,
        shareApi.middleware,
        statsApi.middleware,
        stripeConnectApi.middleware,
        tablesApi.middleware,
        languagesApi.middleware,
      ]),
    devTools: IS_DEVELOPMENT,
  });

export const wrapper = createWrapper<AppStore>(makeStore);
export const persistor = persistStore(makeStore());

export type AppStore = ReturnType<typeof makeStore>;
// Infer the `RootState` and `AppDispatch` types from the store itself
export type AppState = ReturnType<AppStore['getState']>;
export type AppDispatch = AppStore['dispatch'];
